// src/components/OrganizationManagement/OrganizationTableRow.js
import React from 'react';
import PropTypes from 'prop-types';
import logger from '../../utils/logger';
import OrganizationBadges from './OrganizationBadges';
import OrganizationActions from './OrganizationActions';
import './OrganizationTableRow.css';

/**
 * OrganizationTableRow component for displaying a single organization row
 * Handles organization name, users, ID, installations, created date, and actions
 */
const OrganizationTableRow = ({
  organization,
  isCurrent,
  loading,
  copiedOrgId,
  userProfile,
  onSwitchOrganization,
  onViewUsers,
  onCopyOrgId,
  onOpenInstallationManagement,
  onEdit,
  onAuthorize,
  onUnauthorize,
  onDelete,
  formatDate,
}) => {
  const installationCount = organization.installations?.length || 0;
  const canDelete = installationCount === 0;

  return (
    <tr key={organization.orgId} className={isCurrent ? 'current-organization' : ''}>
      {/* Organization Name */}
      <td>
        <div className="org-name-container">
          <button
            className="org-name-link"
            onClick={() => {
              if (onSwitchOrganization) {
                onSwitchOrganization(organization);
              }
            }}
            title="Go to this organization"
            disabled={loading}
          >
            {organization.name}
          </button>
        </div>
        <OrganizationBadges
          authzStatus={organization.romanworksAuthzStatus}
          isCurrent={isCurrent}
        />
      </td>

      {/* Users Column */}
      <td className="users-column">
        <button
          className="btn-view-org-users"
          onClick={(e) => {
            e.stopPropagation();
            onViewUsers(organization);
          }}
          title={`See Authorized Users for ${organization.name}`}
          disabled={loading}
        >
          👥
        </button>
      </td>

      {/* Organization ID */}
      <td className="organization-id">
        <div className="org-id-container">
          <span className="org-id-text">{organization.orgId}</span>
          <button
            className="btn-copy-org-id"
            onClick={() => onCopyOrgId(organization.orgId)}
            title="Copy Organization ID"
            disabled={loading}
          >
            {copiedOrgId === organization.orgId ? '✓' : '📋'}
          </button>
        </div>
      </td>

      {/* Installations */}
      <td>
        {(() => {
          logger.log(
            `[ORG ${organization.name}] installationCount:`,
            installationCount
          );
          logger.log(
            `[ORG ${organization.name}] onOpenInstallationManagement:`,
            typeof onOpenInstallationManagement,
            !!onOpenInstallationManagement
          );
          return null;
        })()}
        {installationCount}
        {onOpenInstallationManagement && (
          <>
            {' | '}
            <button
              className="manage-installations-link"
              onClick={() => {
                logger.log(
                  `[CLICK] Manage Installations button clicked for ${organization.name}`,
                  { orgId: organization.orgId }
                );
                logger.log(
                  '[CLICK] Calling onOpenInstallationManagement with orgId:',
                  organization.orgId
                );
                logger.log(
                  '[CLICK] onOpenInstallationManagement function:',
                  typeof onOpenInstallationManagement
                );
                onOpenInstallationManagement(organization.orgId);
                logger.log('[CLICK] onOpenInstallationManagement called successfully');
              }}
              disabled={loading}
              title={`Manage Sites for ${organization.name}`}
            >
              🛠️🏢
            </button>
          </>
        )}
      </td>

      {/* Created Date */}
      <td>{formatDate(organization.createdAt)}</td>

      {/* Actions */}
      <td className="actions-cell">
        <OrganizationActions
          organization={organization}
          canDelete={canDelete}
          loading={loading}
          userProfile={userProfile}
          onEdit={onEdit}
          onAuthorize={onAuthorize}
          onUnauthorize={onUnauthorize}
          onDelete={onDelete}
        />
      </td>
    </tr>
  );
};

OrganizationTableRow.propTypes = {
  organization: PropTypes.shape({
    orgId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    romanworksAuthzStatus: PropTypes.string,
    installations: PropTypes.array,
    createdAt: PropTypes.string,
  }).isRequired,
  isCurrent: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  copiedOrgId: PropTypes.string,
  userProfile: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
  onSwitchOrganization: PropTypes.func,
  onViewUsers: PropTypes.func.isRequired,
  onCopyOrgId: PropTypes.func.isRequired,
  onOpenInstallationManagement: PropTypes.func,
  onEdit: PropTypes.func.isRequired,
  onAuthorize: PropTypes.func.isRequired,
  onUnauthorize: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
};

OrganizationTableRow.defaultProps = {
  loading: false,
  copiedOrgId: null,
  userProfile: null,
  onSwitchOrganization: null,
  onOpenInstallationManagement: null,
};

export default OrganizationTableRow;
